<template>
  <nav class="navbar" :class="{ 'is-scrolled': isScrolled }">
    <div class="navbar-container">
      <!-- Logo -->
      <router-link to="/" class="logo">
        <div class="logo-icon">
          <el-icon :size="28" color="white"><Monitor /></el-icon>
        </div>
        <div class="logo-text">
          <span class="logo-title">百炼谷</span>
          <span class="logo-subtitle">百炼谷</span>
        </div>
      </router-link>

      <!-- 导航菜单 -->
      <div v-if="!hideNavMenu" class="nav-menu">
        <router-link 
          v-for="(item, index) in navItems" 
          :key="item.path"
          :to="item.path"
          class="nav-item"
          :class="{ active: isActive(item.path), [`nav-color-${index}`]: true }"
        >
          <div class="nav-icon-wrapper">
            <el-icon :size="20"><component :is="item.icon" /></el-icon>
          </div>
          <span class="nav-label">{{ item.label }}</span>
        </router-link>
      </div>

      <!-- 右侧操作区 -->
      <div class="nav-actions">
        <!-- 用户菜单 -->
        <template v-if="userStore.isLoggedIn">
          <el-dropdown @command="handleCommand" placement="bottom-end" :offset="8" popper-class="user-dropdown-popper">
            <div class="user-menu">
              <div class="avatar-wrapper">
                <el-avatar :size="40" :src="userStore.userInfo?.avatar" class="user-avatar">
                  <el-icon :size="20"><User /></el-icon>
                </el-avatar>
                <div class="level-badge">Lv.1</div>
              </div>
              <div class="user-info">
                <span class="user-name">{{ userStore.userInfo?.nickname || userStore.userInfo?.username }}</span>
                <span class="user-role">{{ userStore.userInfo?.role === 'admin' ? '管理员' : '小学员' }}</span>
              </div>
              <el-icon class="dropdown-icon"><ArrowDown /></el-icon>
            </div>
            <template #dropdown>
              <div class="custom-dropdown">
                <!-- 用户信息头部 -->
                <div class="dropdown-header">
                  <div class="header-content">
                    <el-avatar :size="56" :src="userStore.userInfo?.avatar" class="header-avatar">
                      <el-icon :size="28"><User /></el-icon>
                    </el-avatar>
                    <div class="header-info">
                      <div class="header-name">{{ userStore.userInfo?.nickname || userStore.userInfo?.username }}</div>
                      <div class="header-level">
                        <el-tag size="small" type="warning">Lv.1 编程新手</el-tag>
                      </div>
                    </div>
                  </div>
                  <!-- 隐藏统计区域
                  <div class="header-stats">
                    <div class="header-stat">
                      <div class="stat-num">128</div>
                      <div class="stat-label">通过</div>
                    </div>
                    <div class="header-stat">
                      <div class="stat-num">256</div>
                      <div class="stat-label">提交</div>
                    </div>
                    <div class="header-stat">
                      <div class="stat-num">7</div>
                      <div class="stat-label">连续打卡</div>
                    </div>
                  </div>
                  -->
                </div>

                <!-- 菜单项 -->
                <div class="dropdown-menu">
                  <div class="menu-item" @click="handleCommand('profile')">
                    <div class="item-icon icon-blue">
                      <el-icon><User /></el-icon>
                    </div>
                    <span class="item-text">个人中心</span>
                  </div>
                  <div class="menu-item" @click="handleCommand('feedback')">
                    <div class="item-icon icon-green">
                      <el-icon><ChatDotRound /></el-icon>
                    </div>
                    <span class="item-text">意见反馈</span>
                  </div>
                  <!-- 隐藏学习轨迹和错题本入口
                  <div class="menu-item" @click="handleCommand('learning')">
                    <div class="item-icon icon-green">
                      <el-icon><TrendCharts /></el-icon>
                    </div>
                    <span class="item-text">学习轨迹</span>
                  </div>
                  <div class="menu-item" @click="handleCommand('wrongProblems')">
                    <div class="item-icon icon-orange">
                      <el-icon><Document /></el-icon>
                    </div>
                    <span class="item-text">错题本</span>
                  </div>
                  -->
                </div>

                <!-- 退出按钮 -->
                <div class="dropdown-footer">
                  <div class="logout-btn" @click="handleCommand('logout')">
                    <el-icon><SwitchButton /></el-icon>
                    <span>退出登录</span>
                  </div>
                </div>
              </div>
            </template>
          </el-dropdown>
        </template>

        <!-- 未登录状态 -->
        <template v-else>
          <router-link to="/auth/login" class="login-btn">
            <el-icon><Key /></el-icon>
            <span>登录</span>
          </router-link>
          <router-link to="/auth/register" class="register-btn">
            <span>免费注册</span>
          </router-link>
        </template>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { 
  ArrowDown, 
  User, 
  Monitor,
  Key,
  SwitchButton,
  Collection,
  Reading,
  ChatDotRound
} from '@element-plus/icons-vue'
import { useUserStore } from '@/stores/user'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const isScrolled = ref(false)

// 是否隐藏导航菜单
const hideNavMenu = computed(() => route.meta.hideNavMenu as boolean)

// 监听滚动
if (typeof window !== 'undefined') {
  window.addEventListener('scroll', () => {
    isScrolled.value = window.scrollY > 10
  })
}

const navItems = [
  { path: '/problems', label: '题库', icon: Collection },
  { path: '/courses', label: '课程', icon: Reading }
]

const isActive = (path: string) => {
  return route.path.startsWith(path)
}

const handleCommand = (command: string) => {
  switch (command) {
    case 'profile':
      router.push('/profile')
      break
    case 'feedback':
      router.push('/feedback')
      break
    case 'learning':
      router.push('/learning')
      break
    case 'wrongProblems':
      router.push('/wrong-problems')
      break
    case 'logout':
      userStore.logout()
      router.push('/auth/login')
      break
  }
}
</script>

<style scoped lang="scss">
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 72px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  z-index: 100;
  transition: all 0.3s;

  &.is-scrolled {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }
}

.navbar-container {
  max-width: 1600px;
  height: 100%;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 32px;
}

// Logo
.logo {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  flex-shrink: 0;

  .logo-icon {
    width: 44px;
    height: 44px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  .logo-text {
    display: flex;
    flex-direction: column;

    .logo-title {
      font-weight: 800;
      font-size: 20px;
      color: #1e293b;
      line-height: 1.2;
    }

    .logo-subtitle {
      font-size: 12px;
      color: #94a3b8;
      font-weight: 500;
    }
  }
}

// 导航菜单
.nav-menu {
  display: flex;
  gap: 8px;
  flex: 1;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 12px;
  color: #64748b;
  text-decoration: none;
  font-size: 15px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    color: #3b82f6;
    background: #eff6ff;
  }

  &.active {
    color: #3b82f6;
    background: #dbeafe;

    .nav-icon-wrapper {
      background: #3b82f6;
      color: white;
    }
  }
}

.nav-icon-wrapper {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  transition: all 0.3s ease;
}

// 右侧操作区
.nav-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

// 用户菜单
.user-menu {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 16px 6px 6px;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8fafc;
  border: 2px solid #e2e8f0;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }
}

.avatar-wrapper {
  position: relative;

  .user-avatar {
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .level-badge {
    position: absolute;
    bottom: -4px;
    right: -4px;
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
    color: white;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 50px;
    border: 2px solid white;
  }
}

.user-info {
  display: flex;
  flex-direction: column;

  .user-name {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
  }

  .user-role {
    font-size: 11px;
    color: #94a3b8;
  }
}

.dropdown-icon {
  font-size: 12px;
  color: #94a3b8;
  transition: transform 0.3s ease;
}

.user-menu:hover .dropdown-icon {
  transform: rotate(180deg);
}

// 登录注册按钮
.login-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 12px;
  color: #64748b;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    color: #3b82f6;
    background: #eff6ff;
  }
}

.register-btn {
  display: flex;
  align-items: center;
  padding: 10px 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
  color: white;
  border-radius: 12px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
  }
}

// 自定义下拉菜单
:deep(.user-dropdown-popper) {
  padding: 0 !important;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
}

.custom-dropdown {
  width: 240px;
  background: white;
}

// 下拉头部
.dropdown-header {
  padding: 16px;
  background: white;
  border-bottom: 1px solid #f1f5f9;

  .header-content {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.header-avatar {
  border: 2px solid #f1f5f9;
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .header-name {
    font-weight: 600;
    font-size: 15px;
    color: #1e293b;
  }

  .header-level {
    :deep(.el-tag) {
      border: none;
      background: #fef3c7;
      color: #d97706;
      font-size: 11px;
      padding: 0 8px;
      height: 20px;
      line-height: 18px;
    }
  }
}

// 菜单项
.dropdown-menu {
  padding: 8px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #475569;
  margin-bottom: 2px;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: #f8fafc;
    color: #3b82f6;
  }

  .item-icon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;

    &.icon-blue {
      background: #eff6ff;
      color: #3b82f6;
    }

    &.icon-green {
      background: #f0fdf4;
      color: #22c55e;
    }

    &.icon-orange {
      background: #fff7ed;
      color: #f97316;
    }
  }

  .item-text {
    font-size: 13px;
    font-weight: 500;
  }
}

// 退出按钮
.dropdown-footer {
  padding: 8px;
  border-top: 1px solid #f1f5f9;

  .logout-btn {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #fef2f2;
      color: #ef4444;
    }

    .el-icon {
      font-size: 16px;
    }
  }
}
</style>
